import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { Select, Spin, Typography, Alert, Button, theme as antdTheme } from 'antd';
import { useConfirmAction, useNarrowViewport, space } from '@viq-admin/ui';
import { chrome } from '@viq-admin/theme';
import { superadminFetch } from '../api/dataProvider';
import { queryClient } from '../api/queryClient';
import { getScopeCompanyId, setScopeCompanyId } from '../scope/companyScope';
import { hasUnsavedEdits } from '../scope/unsavedChanges';

/** Sentinel Select value for the "All tenants" (cleared scope) option. */
const ALL_TENANTS = '__all__';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Company {
  id: string;
  name: string;
}

interface CompaniesResponse {
  companies: Company[];
}

// ---------------------------------------------------------------------------
// Context — exposes the active companyId to the rest of the SPA
// ---------------------------------------------------------------------------

interface CompanyScopeContextValue {
  /** Currently selected company id, or null for "All tenants" (union). */
  companyId: string | null;
  /** The full list of available companies (for display, e.g. breadcrumbs). */
  companies: Company[];
  /** Switch the active scope; `null` clears it back to "All tenants". */
  setCompanyId: (id: string | null) => void;
}

const CompanyScopeContext = createContext<CompanyScopeContextValue>({
  companyId: null,
  companies: [],
  setCompanyId: () => undefined,
});

export function useCompanyScope(): CompanyScopeContextValue {
  return useContext(CompanyScopeContext);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Fetches the cross-tenant company list from GET /v1/superadmin/companies and
 * provides the active companyId selection to the subtree.
 *
 * Render this near the top of the authenticated layout so all data calls
 * downstream can read useCompanyScope().companyId and append ?companyId=.
 */
export function CompanyScopeProvider({ children }: { children: React.ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  // Seed from the module scope (restored from localStorage on import) so a
  // reload keeps the operator's chosen tenant.
  const [companyId, setCompanyIdState] = useState<string | null>(() =>
    getScopeCompanyId(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirmAction();

  // Central scope switch: mirror into the module (for non-React fetchers +
  // persistence), update React state, and invalidate EVERY cached query so all
  // scope-aware views refetch under the new scope. "All tenants" → pass null.
  //
  // Switching (or clearing) the scope unmounts every scoped view below,
  // including a scoped detail/edit form mid-edit. The shared DetailPage discard
  // guard cannot see that external change, so an in-flight edit would vanish
  // silently. If any editor has announced unsaved changes (see
  // scope/unsavedChanges), confirm-before-discard here — the same idiom the
  // DetailPage guard uses for Cancel/back — and only commit the switch (and the
  // cache invalidation) once the operator accepts. Cancelling keeps both the
  // current scope and the open edit.
  const setCompanyId = useCallback(
    (id: string | null) => {
      const commit = () => {
        setScopeCompanyId(id);
        setCompanyIdState(id);
        void queryClient.invalidateQueries();
      };
      if (hasUnsavedEdits()) {
        confirm({
          title: 'Discard unsaved changes?',
          content: 'Switching tenant will discard the unsaved edits you have open.',
          okText: 'Discard',
          danger: true,
          onConfirm: async () => commit(),
        });
        return;
      }
      commit();
    },
    [confirm],
  );

  useEffect(() => {
    setLoading(true);
    superadminFetch<CompaniesResponse>('/v1/superadmin/companies')
      .then((res) => {
        const list = res.companies ?? [];
        setCompanies(list);
        const current = getScopeCompanyId();
        if (current && !list.some((c) => c.id === current)) {
          // Persisted tenant no longer exists → fall back to All tenants.
          setCompanyId(null);
        } else if (!current && list.length === 1) {
          // Auto-select if only one company (unlikely in prod, handy in dev).
          setCompanyId(list[0].id);
        }
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message ?? 'Failed to load companies';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [setCompanyId]);

  const value = useMemo(
    () => ({ companyId, companies, setCompanyId }),
    [companyId, companies, setCompanyId],
  );

  if (error) {
    return <Alert type="error" message="Could not load tenant list" description={error} />;
  }

  return (
    <CompanyScopeContext.Provider value={value}>
      {loading ? (
        // Nest pattern (Spin wrapping content): antd's `tip` only renders in the
        // nest/fullscreen patterns, not on a bare <Spin/>.
        <Spin tip="Loading tenants…" style={{ display: 'block', marginTop: space.xxl }}>
          <div style={{ minHeight: 80 }} />
        </Spin>
      ) : (
        children
      )}
    </CompanyScopeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Picker UI — render inside the app header/sidebar
// ---------------------------------------------------------------------------

interface CompanyScopePickerProps {
  /** Additional CSS class for the wrapping span. */
  className?: string;
}

/**
 * "Operate as tenant X" selector.
 *
 * Choosing a company sets the active companyId in CompanyScopeContext.
 * Every tenant-scoped data call appends ?companyId=<chosen> and the
 * backend re-validates it server-side (the client-side selection is
 * belt-and-suspenders UX only).
 *
 * The choice survives reloads, so it must never be easy to forget: while a
 * tenant is chosen the picker is drawn in the warning colour (label and
 * border), and the narrowed list pages add a banner (`CompanyScopeBanner`).
 * "All tenants" is the quiet, normal state.
 */
export function CompanyScopePicker({ className }: CompanyScopePickerProps) {
  const { companies, companyId, setCompanyId } = useCompanyScope();
  // On a phone the picker lives in the IQ Panel drawer, not the header: the
  // label goes above a full-width select there, so a long tenant name fits.
  const narrow = useNarrowViewport();
  const { token } = antdTheme.useToken();
  const scoped = companyId != null;
  const scopedName = scoped ? companies.find((c) => c.id === companyId)?.name : undefined;

  // Map the "All tenants" sentinel (and clear) back to a null scope.
  const handleChange = useCallback(
    (value: string | undefined) =>
      setCompanyId(value && value !== ALL_TENANTS ? value : null),
    [setCompanyId],
  );

  const options = useMemo(
    () => [
      { label: 'All tenants', value: ALL_TENANTS },
      ...companies.map((c) => ({ label: c.name, value: c.id })),
    ],
    [companies],
  );

  return (
    <span
      className={className}
      data-scoped={scoped || undefined}
      title={scoped ? `Scope-aware pages show ${scopedName ?? 'one tenant'} only` : undefined}
      style={
        narrow
          ? { display: 'flex', flexDirection: 'column', gap: space.xs }
          : { display: 'inline-flex', alignItems: 'center', gap: space.sm }
      }
    >
      <Text
        style={{
          whiteSpace: 'nowrap',
          // Gold on the navy header in both themes (colorWarning is the brand
          // gold); the plain label stays the header's own light text.
          color: scoped ? token.colorWarning : chrome.onNavyStrong,
          fontWeight: scoped ? 600 : undefined,
        }}
      >
        Operate as:
      </Text>
      <Select
        // Sized to the window off a phone: a long tenant name ellipsizes
        // instead of pushing the header's account button off screen, and just
        // above the compact-header breakpoint (992px), where the full account
        // name still shows, the box gives back a little more room. From 1250px
        // up it is the original 200px minimum.
        style={
          narrow
            ? { width: '100%' }
            : { minWidth: 'min(200px, 16vw)', maxWidth: 'min(320px, 20vw)' }
        }
        status={scoped ? 'warning' : undefined}
        aria-label="Operate as"
        placeholder="All tenants"
        // Show the sentinel when no tenant is scoped so "All tenants" is visible.
        value={companyId ?? ALL_TENANTS}
        onChange={handleChange}
        onClear={() => setCompanyId(null)}
        allowClear
        options={options}
        showSearch
        filterOption={(input, option) =>
          (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Banner — says so on the pages the scope narrows
// ---------------------------------------------------------------------------

/**
 * The pages whose data the "Operate as" scope narrows, found by reading what
 * each one fetches: the scope-aware dataProvider resources (`users`,
 * `conferences`, `questions` — everything not in SCOPE_EXEMPT_RESOURCES) and
 * the views that read `getScopeCompanyId` / `useCompanyScope().companyId`
 * themselves. Exact paths only — list and workspace pages, never a detail
 * page, which is about one record whatever the scope says.
 *
 * - `perTenant`: the page only ever shows one tenant (Questions Inventory has
 *   no all-tenants view; clearing the scope just asks for a tenant again), so
 *   the banner names the tenant without offering to clear it.
 *
 * Deliberately absent:
 * - `/batch` — it is narrowed, but it is an edge-to-edge workspace sized to
 *   exactly the viewport below the header (packages/screens BatchScreen), so
 *   a banner above it would push its footer off the screen. The highlighted
 *   picker in the header is its reminder.
 * - `/leads` — the Leads list filters by its own company filter and the URL,
 *   not by the scope.
 * - `/companies`, `/toolkits`, `/integrations`, AI prompts, Scan Paths — not
 *   tenant data, or exempt from the scope by design.
 */
const SCOPE_NARROWED_PAGES: Record<string, { perTenant?: boolean }> = {
  '/live-feed': {},
  '/users': {},
  '/conferences': {},
  '/conferences/archived': {},
  '/telemetry': {},
  '/questions': { perTenant: true },
};

/**
 * A slim banner at the top of a scope-narrowed page while a tenant is chosen
 * in "Operate as": which tenant, and (where an all-tenants view exists) a
 * one-click way back to it. Renders nothing anywhere else, so the shell can
 * mount it above every page. Must render inside the router.
 */
export function CompanyScopeBanner() {
  const { companies, companyId, setCompanyId } = useCompanyScope();
  const { pathname } = useLocation();
  const page = SCOPE_NARROWED_PAGES[pathname.replace(/\/+$/, '') || '/'];
  if (!companyId || !page) return null;

  const name = companies.find((c) => c.id === companyId)?.name ?? 'one tenant';
  return (
    <Alert
      type="warning"
      showIcon
      role="status"
      style={{ marginBottom: space.lg, paddingBlock: space.xs }}
      message={
        page.perTenant ? (
          <>
            Showing <strong>{name}</strong>’s inventory — change tenant with “Operate as”.
          </>
        ) : (
          <>
            Showing <strong>{name}</strong> only — “Operate as” is set.
          </>
        )
      }
      action={
        page.perTenant ? undefined : (
          <Button size="small" onClick={() => setCompanyId(null)}>
            Show all tenants
          </Button>
        )
      }
    />
  );
}
